import React from 'react';

import { DisclosurePanel } from '../components/AppShellPrimitives';
import { buildRunTraceHref } from '../data';

const ROLE_LABELS = {
  suspicious: 'Suspicious',
  near_miss: 'Near miss',
  control: 'Control',
};

const ROLE_ORDER = ['suspicious', 'near_miss', 'control'];

const TRACE_REVIEW_STATUS_KEY_PREFIX = 'agentic-blue:trace-pack-review';

const TRACE_REVIEW_STATUS_LABELS = {
  not_opened: 'Not opened',
  opened: 'Opened',
  flagged: 'Flagged',
  dismissed: 'Dismissed',
};

function roleEntries(pack, role) {
  return (pack?.entries || []).filter((entry) => entry.role === role);
}

function traceReviewStorageKey(pack, selectedSystem) {
  return `${TRACE_REVIEW_STATUS_KEY_PREFIX}:${selectedSystem?.id || 'system'}:${pack?.source_finding_id || 'workflow'}`;
}

function traceReviewEntryKey(entry) {
  return [entry.role, entry.run_id, entry.primary_step_id || 'run'].join(':');
}

function readTraceReviewState(pack, selectedSystem) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(traceReviewStorageKey(pack, selectedSystem));
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
}

function writeTraceReviewState(pack, selectedSystem, value) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(traceReviewStorageKey(pack, selectedSystem), JSON.stringify(value));
  } catch (error) {
    // Local review state is a convenience only; ignore storage failures.
  }
}

function humanize(value) {
  return String(value || '')
    .split('_')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function traceEntryHref(entry, pack, selectedSystem) {
  return buildRunTraceHref({
    systemId: selectedSystem?.id,
    runId: entry.run_id,
    primaryStepId: entry.primary_step_id,
    comparisonStepIds: entry.comparison_step_ids || [],
    sourceFindingId: pack?.source_finding_id,
  });
}

function preferredWorkspaceEntry(pack) {
  return roleEntries(pack, 'suspicious')[0] || pack?.entries?.[0] || null;
}

function openedCount(pack, reviewState) {
  return (pack?.entries || []).filter((entry) => {
    const status = reviewState[traceReviewEntryKey(entry)] || 'not_opened';
    return status !== 'not_opened';
  }).length;
}

function TraceReviewWorkspaceLaunch({ pack, selectedSystem, reviewState, onMarkOpened }) {
  const entry = preferredWorkspaceEntry(pack);
  if (!entry) {
    return null;
  }
  const counts = ROLE_ORDER.map((role) => ({
    role,
    label: ROLE_LABELS[role],
    count: roleEntries(pack, role).length,
  })).filter((item) => item.count > 0);
  const href = traceEntryHref(entry, pack, selectedSystem);
  const opened = openedCount(pack, reviewState);

  return (
    <section className="trace-pack-workspace-launch">
      <div>
        <p className="chapter-kicker">Trace workspace</p>
        <h4>Review selected traces in the workspace</h4>
        <p>Start with the suspicious trace, then compare it against near misses and controls in the waterfall view.</p>
      </div>
      <div className="trace-pack-workspace-metrics" aria-label="Trace workspace summary">
        {counts.map((item) => (
          <div key={item.role}>
            <strong>{item.count}</strong>
            <span>{item.label}</span>
          </div>
        ))}
        <div>
          <strong>{opened}</strong>
          <span>Opened</span>
        </div>
      </div>
      <a className="primary-button trace-pack-workspace-link" href={href} onClick={() => onMarkOpened(entry)}>
        Open trace workspace
      </a>
    </section>
  );
}

function CommonFactorsSummary({ factors }) {
  if (!factors?.length) {
    return null;
  }
  return (
    <section className="trace-pack-common-summary" aria-label="Common factors">
      <div className="workflow-mode-header">
        <div>
          <p className="chapter-kicker">Common factors</p>
          <h4>What the selected traces have in common</h4>
        </div>
        <span className="chip">{factors.length} factors</span>
      </div>
      <ol className="trace-pack-common-factor-list">
        {factors.map((factor, index) => (
          <li className="trace-pack-common-factor-card" key={`${factor.factor_type}-${factor.label}-${factor.value}`}>
            <span className="trace-pack-common-factor-rank">{String(index + 1).padStart(2, '0')}</span>
            <div>
              <span>{factor.factor_type}</span>
              <strong>{factor.label}</strong>
              <p>{factor.value}</p>
              <small>Seen in {factor.support_count} selected trace{factor.support_count === 1 ? '' : 's'}</small>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function FlagIcon() {
  return (
    <svg aria-hidden="true" className="trace-pack-action-icon" viewBox="0 0 24 24">
      <path d="M6 21V4" />
      <path d="M6 5h11l-2 4 2 4H6" />
    </svg>
  );
}

function DismissIcon() {
  return (
    <svg aria-hidden="true" className="trace-pack-action-icon" viewBox="0 0 24 24">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function OpenTraceIcon() {
  return (
    <svg aria-hidden="true" className="trace-pack-action-icon trace-pack-open-icon" viewBox="0 0 24 24">
      <path d="M8 8h8v8" />
      <path d="M8 16 16 8" />
      <path d="M11 8h5v5" />
    </svg>
  );
}

function TracePackEntry({ entry, pack, selectedSystem, reviewStatus, onReviewStatusChange }) {
  const href = traceEntryHref(entry, pack, selectedSystem);
  const statusLabel = TRACE_REVIEW_STATUS_LABELS[reviewStatus] || TRACE_REVIEW_STATUS_LABELS.not_opened;
  const isOpened = reviewStatus !== 'not_opened';
  const markOpened = () => {
    if (reviewStatus === 'not_opened') {
      onReviewStatusChange('opened');
    }
  };
  const toggleFlagged = () => {
    onReviewStatusChange(reviewStatus === 'flagged' ? 'opened' : 'flagged');
  };
  const toggleDismissed = () => {
    onReviewStatusChange(reviewStatus === 'dismissed' ? 'opened' : 'dismissed');
  };
  const metaItems = [
    entry.status ? `Run ${humanize(entry.status)}` : null,
  ].filter(Boolean);

  return (
    <article className={`detail-card trace-pack-entry trace-pack-entry-${entry.role}`}>
      <div className="trace-pack-entry-main">
        <div>
          <strong>
            {entry.label}
            {entry.caveat ? (
              <span className="trace-pack-title-info" tabIndex="0" title={entry.caveat} aria-label={entry.caveat}>
                i
              </span>
            ) : null}
          </strong>
          <p>{entry.reason}</p>
          {metaItems.length ? <p className="trace-pack-entry-meta">{metaItems.join(' · ')}</p> : null}
        </div>
      </div>
      <div className="trace-pack-entry-rail">
        <div className="trace-pack-entry-status-row">
          <span
            aria-label={isOpened ? 'Trace opened' : 'Trace not opened'}
            className={`trace-pack-open-indicator${isOpened ? ' is-opened' : ''}`}
            title={statusLabel}
          />
          {reviewStatus === 'flagged' || reviewStatus === 'dismissed' ? (
            <span className={`chip trace-pack-review-status trace-pack-review-status-${reviewStatus}`}>
              {statusLabel}
            </span>
          ) : null}
        </div>
        <a aria-label="Open trace workspace" className="secondary-button trace-pack-icon-button trace-pack-open-button" href={href} onClick={markOpened} rel="noreferrer" target="_blank" title="Open trace workspace">
          <OpenTraceIcon />
        </a>
        <button
          aria-label={reviewStatus === 'flagged' ? 'Remove flag' : 'Flag trace'}
          className={reviewStatus === 'flagged' ? 'secondary-button trace-pack-icon-button is-selected' : 'secondary-button trace-pack-icon-button'}
          onClick={toggleFlagged}
          title={reviewStatus === 'flagged' ? 'Remove flag' : 'Flag trace'}
          type="button"
        >
          <FlagIcon />
        </button>
        <button
          aria-label={reviewStatus === 'dismissed' ? 'Restore trace' : 'Dismiss trace'}
          className={reviewStatus === 'dismissed' ? 'secondary-button trace-pack-icon-button is-selected' : 'secondary-button trace-pack-icon-button'}
          onClick={toggleDismissed}
          title={reviewStatus === 'dismissed' ? 'Restore trace' : 'Dismiss trace'}
          type="button"
        >
          <DismissIcon />
        </button>
      </div>
    </article>
  );
}

function limitForRole(maxEntriesByRole, role) {
  const limit = maxEntriesByRole?.[role];
  return typeof limit === 'number' && Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : null;
}

export default function SuspiciousTracePackPanel({
  pack,
  selectedSystem,
  defaultOpen = false,
  visibleRoles = ROLE_ORDER,
  maxEntriesByRole = {},
}) {
  const [reviewState, setReviewState] = React.useState(() => readTraceReviewState(pack, selectedSystem));

  React.useEffect(() => {
    setReviewState(readTraceReviewState(pack, selectedSystem));
  }, [pack, selectedSystem]);

  const updateEntryReviewStatus = (entry, nextStatus) => {
    const entryKey = traceReviewEntryKey(entry);
    setReviewState((current) => {
      const next = {
        ...current,
        [entryKey]: nextStatus,
      };
      writeTraceReviewState(pack, selectedSystem, next);
      return next;
    });
  };

  const markEntryOpened = (entry) => {
    const entryKey = traceReviewEntryKey(entry);
    setReviewState((current) => {
      if (current[entryKey] && current[entryKey] !== 'not_opened') {
        return current;
      }
      const next = {
        ...current,
        [entryKey]: 'opened',
      };
      writeTraceReviewState(pack, selectedSystem, next);
      return next;
    });
  };

  if (!pack) {
    return null;
  }

  const rolesToRender = ROLE_ORDER.filter((role) => visibleRoles.includes(role));
  const traceCount = pack.entries?.length || 0;
  const visibleTraceCount = rolesToRender.reduce((count, role) => count + roleEntries(pack, role).length, 0);

  if (pack.status !== 'available') {
    return (
      <DisclosurePanel
        className="detail-section trace-pack-panel trace-pack-disclosure"
        id="trace-review-section"
        title="Trace review"
        summaryMeta="Unavailable"
        defaultOpen={defaultOpen}
      >
        <div className="chapter-header">
          <div>
            <p className="chapter-kicker">Trace review</p>
            <h3>Suspicious trace pack</h3>
            <p className="panel-copy">{pack.summary}</p>
          </div>
        </div>
        <div className="empty-state-card">
          <strong>Trace pack unavailable</strong>
          <p>Agentic Blue needs stronger run-level lineage before it can select suspicious traces and controls.</p>
          {pack.next_steps?.length ? (
            <ul>
              {pack.next_steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          ) : null}
        </div>
      </DisclosurePanel>
    );
  }

  return (
    <DisclosurePanel
      className="detail-section trace-pack-panel trace-pack-disclosure"
      id="trace-review-section"
      title="Trace review"
      summaryMeta={visibleTraceCount === traceCount ? `${traceCount} traces` : `${visibleTraceCount} shown`}
      defaultOpen={defaultOpen}
    >
      <div className="chapter-header">
        <div>
          <p className="chapter-kicker">Trace review</p>
          <h3>Suspicious trace pack</h3>
          <p className="panel-copy">{pack.summary}</p>
        </div>
      </div>

      <TraceReviewWorkspaceLaunch
        onMarkOpened={markEntryOpened}
        pack={pack}
        reviewState={reviewState}
        selectedSystem={selectedSystem}
      />

      <CommonFactorsSummary factors={pack.common_factors} />

      {pack.caveats?.length ? (
        <div className="chip-row trace-pack-caveats" aria-label="Trace pack caveats">
          {pack.caveats.map((caveat) => (
            <span className="chip chip-warning" key={caveat.code}>{caveat.label}</span>
          ))}
        </div>
      ) : null}

      {rolesToRender.map((role) => {
        const entries = roleEntries(pack, role);
        if (!entries.length) {
          return null;
        }
        const limit = limitForRole(maxEntriesByRole, role);
        const displayedEntries = limit ? entries.slice(0, limit) : entries;
        const hiddenCount = entries.length - displayedEntries.length;
        return (
          <div className="trace-pack-role-group" key={role}>
            <h4>
              <span>{ROLE_LABELS[role]} trace group</span>
              {hiddenCount > 0 ? <small>Showing {displayedEntries.length} of {entries.length}</small> : null}
            </h4>
            <div className="trace-pack-entry-list">
              {displayedEntries.map((entry) => (
                <TracePackEntry
                  entry={entry}
                  key={`${entry.role}-${entry.run_id}`}
                  onReviewStatusChange={(nextStatus) => updateEntryReviewStatus(entry, nextStatus)}
                  pack={pack}
                  reviewStatus={reviewState[traceReviewEntryKey(entry)] || 'not_opened'}
                  selectedSystem={selectedSystem}
                />
              ))}
            </div>
          </div>
        );
      })}
    </DisclosurePanel>
  );
}
